import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VisibilitySensor from "react-visibility-sensor";
import { CircularLoader, CustomChips, CustomIconButton, CustomTextField, CustomTooltip, icon } from "..";
import { ApiService, DocumentService, ReqViewService } from "../../../api";
import { translate } from "../../providers";
import { debounce, isNonEmptyArray } from "../../utils";
import styles from "./VersionComparisonPanel.module.css";

const debouncedScrollSync = debounce((callback, ratio) => {
	callback(ratio);
}, 5);

const debounceFetchPages = debounce((callback, start, end) => {
	callback(start, end);
}, 100);

const renderPositionStyle = (metadata, pageWidth, pageHeight) => {
	const { x1, x2, y1, y2 } = metadata;
	return {
		left: pageWidth * x1 - 5,
		top: pageHeight * y1 - 5,
		width: pageWidth * (x2 - x1) + 10,
		height: pageHeight * (y2 - y1) + 10,
	};
};

function ContentArea({
	rectangle,
	width,
	height,
	isHovered,
	isSelected,
	isInitialSelected,
	onMouseEnter,
	onMouseLeave,
	onClick,
}) {
	if (
		!rectangle ||
		typeof rectangle.x1 !== "number" ||
		typeof rectangle.y1 !== "number" ||
		typeof rectangle.x2 !== "number" ||
		typeof rectangle.y2 !== "number"
	) {
		return null;
	}

	const style = renderPositionStyle(rectangle, width, height);
	const contentId = rectangle?.informationId || rectangle?.info_id || rectangle?.contentId;

	let zIndex = 1;
	if (isInitialSelected) {
		zIndex = 11;
	} else if (isSelected || isHovered) {
		zIndex = 10;
	}

	return (
		<div
			alt={contentId}
			className={styles.contentArea}
			data-clickable="true"
			data-hovered={isHovered}
			data-info-content="true"
			data-information-id={contentId}
			data-initial-selected={isInitialSelected}
			data-is-search-result={isSelected}
			data-page={rectangle?.pageStart}
			data-req-content="false"
			role="presentation"
			style={{
				...style,
				zIndex,
			}}
			onClick={() => onClick?.(rectangle)}
			onMouseEnter={() => onMouseEnter?.(rectangle)}
			onMouseLeave={onMouseLeave}
		/>
	);
}

function SimpleContentLayer({
	metadata,
	width,
	height,
	hoveredContent,
	selectedContent,
	initialSelectedContent,
	onHoverContent,
	onClickContent,
}) {
	if (!isNonEmptyArray(metadata)) {
		return null;
	}

	const computeAreaProperties = (area) => {
		const areaId = area?.informationId || area?.info_id || area?.contentId;
		const isHovered =
			(hoveredContent?.informationId && hoveredContent.informationId === area?.informationId) ||
			(hoveredContent?.info_id && hoveredContent.info_id === area?.info_id);
		const isSelected =
			(selectedContent?.informationId && selectedContent.informationId === area?.informationId) ||
			(selectedContent?.info_id && selectedContent.info_id === area?.info_id);
		const isInitialSelected =
			(initialSelectedContent?.informationId && initialSelectedContent.informationId === area?.informationId) ||
			(initialSelectedContent?.info_id && initialSelectedContent.info_id === area?.info_id);
		return { areaId, isHovered, isSelected, isInitialSelected };
	};

	return (
		<>
			{metadata.map((area) => {
				const props = computeAreaProperties(area);

				if (isNonEmptyArray(area?.rectangles)) {
					const { rectangle: rec, ...rest } = area;
					const { contentId } = area.rectangles[0];

					return (
						<ContentArea
							key={props.areaId}
							height={height}
							isHovered={props.isHovered}
							isInitialSelected={props.isInitialSelected}
							isSelected={props.isSelected}
							rectangle={{ ...rec, contentId, ...rest }}
							width={width}
							onClick={onClickContent}
							onMouseEnter={onHoverContent}
							onMouseLeave={() => onHoverContent(null)}
						/>
					);
				}

				return (
					<ContentArea
						key={props.areaId}
						height={height}
						isHovered={props.isHovered}
						isInitialSelected={props.isInitialSelected}
						isSelected={props.isSelected}
						rectangle={area}
						width={width}
						onClick={onClickContent}
						onMouseEnter={onHoverContent}
						onMouseLeave={() => onHoverContent(null)}
					/>
				);
			})}
		</>
	);
}

export default function ComparisonDocumentViewer({
	documentId,
	documentName,
	scrollRef,
	scrollToRatio,
	scrollToPage,
	scrollToPosition,
	onScroll,
	onScrollPosition,
	onPageDimensionsChange,
	onContentClick,
	siblingScrollRef,
	siblingPageDimensions,
	siblingZoom,
	scrollLocked,
	disableSyncRef,
	headerExtra,
	showZoomControls = true,
	showPageInfo = true,
	showVersionInfo = true,
	enableContentClick = true,
	initialSelectedContent = null,
	zoom: externalZoom = null,
	onZoomChange = null,
	translationPrefix = "common:cmp-details.option-panel.versioning.comparison",
	isLatestVersion = false,
}) {
	const cancelTokenSourceRef = useRef(null);
	const isScrollingSyncRef = useRef(false);
	const internalScrollRef = useRef(null);
	const pageHistoryRef = useRef([]);
	const metadataHistoryRef = useRef([]);
	const containerRef = useRef(null);

	const [internalZoom, setInternalZoom] = useState(100);
	const zoom = externalZoom !== null ? externalZoom : internalZoom;
	const [currentPage, setCurrentPage] = useState(1);
	const [numberOfPages, setNumberOfPages] = useState(0);
	const [pageDimensions, setPageDimensions] = useState([]);
	const [content, setContent] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [containerMounted, setContainerMounted] = useState(false);
	const [hoveredContent, setHoveredContent] = useState(null);
	const [selectedContent, setSelectedContent] = useState(null);
	const initialScrollAppliedRef = useRef(false);
	const viewportCenterBeforeZoomRef = useRef(null);
	const previousZoomRef = useRef(100);
	const containerId = useMemo(() => `comparison_viewer_${documentId || "unknown"}`, [documentId]);
	const zoomBucket = useMemo(() => Math.floor(zoom / 25), [zoom]);

	const handleLoadPage = useCallback(
		(i, errorMessage) => {
			pageHistoryRef.current.push(i);
			setContent((prev) => {
				const exists = prev.find((c) => c.page === i);
				if (!exists) {
					return [...prev, { page: i, isLoading: true }];
				}
				return prev;
			});
			DocumentService.getPage({ docId: documentId }, { page: i }, cancelTokenSourceRef.current.token)
				.then((blob) => {
					const blobImage = URL.createObjectURL(blob);
					setContent((prev) =>
						prev.map((c) => (c.page === i ? { ...c, image: blobImage, isLoading: false } : c))
					);
				})
				.catch((error) => {
					console.error(errorMessage, i, error);
					setContent((prev) => prev.map((c) => (c.page === i ? { ...c, isLoading: false, error: true } : c)));
				});
		},
		[documentId]
	);
	const initializeContainer = useCallback(() => {
		if (internalScrollRef.current) {
			containerRef.current = internalScrollRef.current;
			setContainerMounted(true);
		}
	}, []);
	useEffect(() => {
		if (initialSelectedContent) {
			setSelectedContent(initialSelectedContent);
		}
	}, [initialSelectedContent]);
	useEffect(() => {
		initialScrollAppliedRef.current = false;
	}, [documentId]);
	useEffect(() => {
		if (onPageDimensionsChange && pageDimensions.length > 0) {
			onPageDimensionsChange(pageDimensions, zoom);
		}
	}, [pageDimensions, zoom, onPageDimensionsChange]);
	useEffect(() => {
		if (scrollRef && internalScrollRef.current) {
			scrollRef.current = internalScrollRef.current;
		}
	}, [scrollRef, containerMounted]);
	useEffect(() => {
		initializeContainer();
	}, [initializeContainer]);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		if (documentId && cancelTokenSourceRef.current) {
			setIsLoading(true);
			setContent([]);
			pageHistoryRef.current = [];
			metadataHistoryRef.current = [];
			setCurrentPage(1);
			setContainerMounted(false);
			DocumentService.getDocumentPhysicalDetails({ docId: documentId }, cancelTokenSourceRef.current.token)
				.then((data) => {
					const { numberOfPages: pages, pageDimensions: dimensions, pageHigh, pageWidth } = data || {};
					setNumberOfPages(pages || 0);
					let pDimensions = dimensions;
					if (!isNonEmptyArray(dimensions)) {
						pDimensions = Array.from({ length: pages || 0 }).fill({
							width: pageWidth,
							height: pageHigh,
						});
					}
					let accumulatedTop = 0;
					const pagesWithTop = (pDimensions || []).map((page, index) => {
						const pageWithTop = { ...page, top: accumulatedTop, page: index + 1 };
						accumulatedTop += page.height;
						return pageWithTop;
					});
					setPageDimensions(pagesWithTop);
					setIsLoading(false);
					const initialPagesToLoad = Math.min(5, pages || 0);
					for (let i = 1; i <= initialPagesToLoad; i++) {
						setImmediate(() => {
							if (!pageHistoryRef.current.includes(i)) {
								handleLoadPage(i, "Failed to load initial page:");
							}
							if (!metadataHistoryRef.current.includes(i)) {
								metadataHistoryRef.current.push(i);
								ReqViewService.getContents(
									{ docId: documentId },
									{ page: i },
									cancelTokenSourceRef.current.token
								)
									.then((contentData) => {
										let informations = contentData;
										if (
											contentData &&
											typeof contentData === "object" &&
											!Array.isArray(contentData)
										) {
											if (Object.prototype.hasOwnProperty.call(contentData, "informations")) {
												informations = contentData.informations;
											}
										}
										setContent((prev) => {
											const existingEntry = prev.find((c) => c.page === i);
											if (existingEntry) {
												return prev.map((c) =>
													c.page === i ? { ...c, metadata: informations } : c
												);
											}
											return [...prev, { page: i, metadata: informations }];
										});
									})
									.catch((error) => {
										console.error("Failed to load initial metadata:", i, error);
									});
							}
						});
					}
					setTimeout(() => {
						initializeContainer();
					}, 100);
				})
				.catch((error) => {
					console.error("Failed to load document metadata:", error);
					setIsLoading(false);
				});
		}
	}, [documentId, handleLoadPage, initializeContainer]);

	useEffect(() => {
		if (!isLoading && containerMounted && internalScrollRef?.current) {
			if (!scrollToPage && !initialScrollAppliedRef.current) {
				internalScrollRef.current.scrollTop = 0;
				setCurrentPage(1);
			}
		}
	}, [isLoading, containerMounted, scrollToPage]);

	useEffect(() => {
		if (isLoading || !containerMounted || content.length === 0) {
			return undefined;
		}

		const retryFailedPages = () => {
			const failedPages = content.filter((c) => c.error && !c.isLoading);
			failedPages.forEach((failedPage) => {
				pageHistoryRef.current = pageHistoryRef.current.filter((p) => p !== failedPage.page);
				setContent((prev) =>
					prev.map((c) => (c.page === failedPage.page ? { ...c, error: false, isLoading: false } : c))
				);
			});
		};

		const intervalId = setInterval(retryFailedPages, 3000);

		return () => clearInterval(intervalId);
	}, [isLoading, containerMounted, content]);

	const loadPage = useCallback(
		(page, retryCount = 0) => {
			if (retryCount === 0 && pageHistoryRef.current.includes(page)) {
				return;
			}
			if (!cancelTokenSourceRef?.current || !documentId) {
				return;
			}

			if (retryCount === 0) {
				pageHistoryRef.current.push(page);
			}

			setContent((prev) => {
				const existingEntry = prev.find((c) => c.page === page);
				if (existingEntry) {
					return prev.map((c) => (c.page === page ? { ...c, isLoading: true, error: false } : c));
				}
				return [...prev, { page, isLoading: true }];
			});

			DocumentService.getPage({ docId: documentId }, { page }, cancelTokenSourceRef.current.token)
				.then((blob) => {
					const blobImage = URL.createObjectURL(blob);
					setContent((prev) =>
						prev.map((c) =>
							c.page === page ? { ...c, image: blobImage, isLoading: false, error: false } : c
						)
					);
				})
				.catch((error) => {
					console.error("Failed to load page:", page, "attempt:", retryCount + 1, error);
					if (retryCount < 2) {
						pageHistoryRef.current = pageHistoryRef.current.filter((p) => p !== page);
						setTimeout(() => {
							loadPage(page, retryCount + 1);
						}, 300 * (retryCount + 1));
					} else {
						setContent((prev) =>
							prev.map((c) => (c.page === page ? { ...c, isLoading: false, error: true } : c))
						);
					}
				});
		},
		[documentId]
	);

	const loadMetadata = useCallback(
		(page) => {
			if (metadataHistoryRef.current.includes(page)) {
				return;
			}
			if (!cancelTokenSourceRef?.current || !documentId) {
				return;
			}
			metadataHistoryRef.current.push(page);

			ReqViewService.getContents({ docId: documentId }, { page }, cancelTokenSourceRef.current.token)
				.then((data) => {
					let informations = data;
					if (data && typeof data === "object" && !Array.isArray(data)) {
						if (Object.prototype.hasOwnProperty.call(data, "informations")) {
							informations = data.informations;
						}
					}
					setContent((prev) => {
						const existingEntry = prev.find((c) => c.page === page);
						if (existingEntry) {
							return prev.map((c) => (c.page === page ? { ...c, metadata: informations } : c));
						}
						return [...prev, { page, metadata: informations }];
					});
				})
				.catch((error) => {
					console.error("Failed to load page metadata:", page, error);
				});
		},
		[documentId]
	);

	const handleGetPages = useCallback(
		(start, end) => {
			for (let i = start; i <= end; i++) {
				if (i > 0 && i <= numberOfPages) {
					loadPage(i);
					loadMetadata(i);
				}
			}
		},
		[loadPage, loadMetadata, numberOfPages]
	);

	const visiblePagesRef = useRef(new Set());

	const handleVisibilityChange = useCallback(
		(isVisible, pageIndex) => {
			const page = pageIndex + 1;
			if (isVisible) {
				visiblePagesRef.current.add(page);
				debounceFetchPages(handleGetPages, Math.max(1, page - 2), Math.min(numberOfPages, page + 8));
			} else {
				visiblePagesRef.current.delete(page);
			}
		},
		[handleGetPages, numberOfPages]
	);

	useEffect(() => {
		if (
			scrollToPosition !== null &&
			scrollToPosition !== undefined &&
			internalScrollRef?.current &&
			!isScrollingSyncRef.current &&
			pageDimensions.length > 0
		) {
			const { page: targetPage, offsetInPage } = scrollToPosition;

			const clampedPage = Math.min(Math.max(1, targetPage), numberOfPages);
			if (clampedPage < 1) {
				return;
			}

			const container = internalScrollRef.current;
			const targetPageDimension = pageDimensions[clampedPage - 1];

			if (!targetPageDimension) {
				return;
			}

			const pageTopPosition = (targetPageDimension.top * zoom) / 100;
			const pageHeight = (targetPageDimension.height * zoom) / 100;
			const offsetWithinPage = pageHeight * offsetInPage;
			const targetScrollTop = pageTopPosition + offsetWithinPage;

			isScrollingSyncRef.current = true;
			requestAnimationFrame(() => {
				container.scrollTop = targetScrollTop;
				setTimeout(() => {
					isScrollingSyncRef.current = false;
				}, 50);
			});
		}
	}, [scrollToPosition, numberOfPages, pageDimensions, zoom]);

	const pendingScrollPageRef = useRef(scrollToPage);
	const hasAppliedInitialScrollRef = useRef(false);

	useEffect(() => {
		if (scrollToPage !== null && scrollToPage !== undefined) {
			pendingScrollPageRef.current = scrollToPage;
			hasAppliedInitialScrollRef.current = false;
		}
	}, [scrollToPage]);

	useEffect(() => {
		const targetPage = pendingScrollPageRef.current;
		if (
			targetPage !== null &&
			targetPage !== undefined &&
			!hasAppliedInitialScrollRef.current &&
			internalScrollRef?.current &&
			pageDimensions.length > 0 &&
			!isLoading &&
			containerMounted &&
			numberOfPages > 0
		) {
			const clampedPage = Math.min(targetPage, numberOfPages);
			if (clampedPage < 1) {
				return;
			}

			hasAppliedInitialScrollRef.current = true;

			const container = internalScrollRef.current;
			const targetPageDimension = pageDimensions[clampedPage - 1];

			if (targetPageDimension) {
				initialScrollAppliedRef.current = true;

				const startPage = Math.max(1, clampedPage - 2);
				const endPage = Math.min(numberOfPages, clampedPage + 5);
				for (let i = startPage; i <= endPage; i++) {
					if (!pageHistoryRef.current.includes(i) && cancelTokenSourceRef?.current && documentId) {
						handleLoadPage(i, "Failed to load page on scroll:");
					}
					if (!metadataHistoryRef.current.includes(i) && cancelTokenSourceRef?.current && documentId) {
						metadataHistoryRef.current.push(i);
						ReqViewService.getContents(
							{ docId: documentId },
							{ page: i },
							cancelTokenSourceRef.current.token
						)
							.then((data) => {
								let informations = data;
								if (data && typeof data === "object" && !Array.isArray(data)) {
									if (Object.prototype.hasOwnProperty.call(data, "informations")) {
										informations = data.informations;
									}
								}
								setContent((prev) => {
									const existingEntry = prev.find((c) => c.page === i);
									if (existingEntry) {
										return prev.map((c) => (c.page === i ? { ...c, metadata: informations } : c));
									}
									return [...prev, { page: i, metadata: informations }];
								});
							})
							.catch((error) => {
								console.error("Failed to load metadata on scroll:", i, error);
							});
					}
				}

				const targetScrollTop = (targetPageDimension.top * zoom) / 100;
				isScrollingSyncRef.current = true;
				requestAnimationFrame(() => {
					container.scrollTop = targetScrollTop;
					setCurrentPage(clampedPage);
					setTimeout(() => {
						isScrollingSyncRef.current = false;
					}, 100);
				});
			}
		}
	}, [isLoading, containerMounted, pageDimensions, numberOfPages, zoom, documentId, handleLoadPage, scrollToPage]);

	useEffect(() => {
		if (
			scrollToRatio !== null &&
			scrollToRatio !== undefined &&
			internalScrollRef?.current &&
			!isScrollingSyncRef.current
		) {
			const container = internalScrollRef.current;
			const { scrollHeight, clientHeight } = container;
			const maxScroll = scrollHeight - clientHeight;
			const targetScrollTop = maxScroll * scrollToRatio;

			isScrollingSyncRef.current = true;
			requestAnimationFrame(() => {
				container.scrollTop = targetScrollTop;
				setTimeout(() => {
					isScrollingSyncRef.current = false;
				}, 20);
			});
		}
	}, [scrollToRatio]);

	const handleScroll = useCallback(() => {
		if (!internalScrollRef?.current || isScrollingSyncRef.current) {
			return;
		}

		const container = internalScrollRef.current;
		const { scrollTop, scrollHeight, clientHeight: viewportHeight } = container;

		if (pageDimensions.length > 0 && numberOfPages > 0) {
			const isAtTop = scrollTop <= 5;
			const isAtBottom = scrollTop + viewportHeight >= scrollHeight - 5;
			let detectedPage = 1;
			if (isAtBottom) {
				detectedPage = numberOfPages;
			} else if (!isAtTop) {
				const readingPosition = scrollTop + viewportHeight * 0.2;

				for (let i = 0; i < pageDimensions.length; i++) {
					const pageTop = (pageDimensions[i].top * zoom) / 100;
					const pageHeight = (pageDimensions[i].height * zoom) / 100;
					const pageBottom = pageTop + pageHeight;

					if (readingPosition >= pageTop && readingPosition < pageBottom) {
						detectedPage = i + 1;
						break;
					}

					if (pageTop > readingPosition) {
						detectedPage = Math.max(1, i);
						break;
					}

					if (i === pageDimensions.length - 1) {
						detectedPage = numberOfPages;
					}
				}
			}
			if (detectedPage >= 1 && detectedPage <= numberOfPages) {
				setCurrentPage(detectedPage);
			}
			const viewportCenter = scrollTop + viewportHeight / 2;
			let nearestPage = 1;
			let closestDist = Infinity;
			for (let i = 0; i < pageDimensions.length; i++) {
				const pageTop = (pageDimensions[i].top * zoom) / 100;
				const pageHeight = (pageDimensions[i].height * zoom) / 100;
				const pageCenter = pageTop + pageHeight / 2;
				const dist = Math.abs(viewportCenter - pageCenter);
				if (dist < closestDist) {
					closestDist = dist;
					nearestPage = i + 1;
				}
			}
			const startPage = Math.max(1, nearestPage - 3);
			const endPage = Math.min(numberOfPages, nearestPage + 6);
			for (let p = startPage; p <= endPage; p++) {
				loadPage(p);
				loadMetadata(p);
			}
		}

		if (scrollLocked && !disableSyncRef?.current && siblingScrollRef?.current && pageDimensions.length > 0) {
			let currentScrollPage = 1;
			let pageTopOffset = 0;

			for (let i = 0; i < pageDimensions.length; i++) {
				const pageTop = (pageDimensions[i].top * zoom) / 100;
				const pageHeight = (pageDimensions[i].height * zoom) / 100;
				const pageBottom = pageTop + pageHeight;

				if (scrollTop >= pageTop && scrollTop < pageBottom) {
					currentScrollPage = i + 1;
					pageTopOffset = pageTop;
					break;
				} else if (scrollTop < pageTop) {
					currentScrollPage = Math.max(1, i);
					pageTopOffset = i > 0 ? (pageDimensions[i - 1].top * zoom) / 100 : 0;
					break;
				}

				if (i === pageDimensions.length - 1) {
					currentScrollPage = pageDimensions.length;
					pageTopOffset = pageTop;
				}
			}

			const currentPageDimension = pageDimensions[currentScrollPage - 1];
			const currentPageHeight = ((currentPageDimension?.height ?? 1) * zoom) / 100;
			const offsetInPage = Math.max(0, Math.min(1, (scrollTop - pageTopOffset) / currentPageHeight));

			const siblingDims = siblingPageDimensions || [];
			const siblingTotalPages = siblingDims.length;
			const effectiveZoom = siblingZoom || zoom;

			if (siblingTotalPages > 0 && currentScrollPage <= siblingTotalPages) {
				const siblingPageDim = siblingDims[currentScrollPage - 1];
				if (siblingPageDim) {
					const siblingPageTop = (siblingPageDim.top * effectiveZoom) / 100;
					const siblingPageHeight = (siblingPageDim.height * effectiveZoom) / 100;
					const siblingTargetScroll = siblingPageTop + siblingPageHeight * offsetInPage;

					isScrollingSyncRef.current = true;
					siblingScrollRef.current.scrollTop = siblingTargetScroll;
					Promise.resolve().then(() => {
						isScrollingSyncRef.current = false;
					});
				}
			}
		}

		if (typeof onScrollPosition === "function" && pageDimensions.length > 0) {
			let currentScrollPage = 1;
			let pageTopOffset = 0;

			for (let i = 0; i < pageDimensions.length; i++) {
				const pageTop = (pageDimensions[i].top * zoom) / 100;
				const pageHeight = (pageDimensions[i].height * zoom) / 100;
				const pageBottom = pageTop + pageHeight;

				if (scrollTop >= pageTop && scrollTop < pageBottom) {
					currentScrollPage = i + 1;
					pageTopOffset = pageTop;
					break;
				} else if (scrollTop < pageTop) {
					currentScrollPage = Math.max(1, i);
					pageTopOffset = i > 0 ? (pageDimensions[i - 1].top * zoom) / 100 : 0;
					break;
				}

				if (i === pageDimensions.length - 1) {
					currentScrollPage = pageDimensions.length;
					pageTopOffset = pageTop;
				}
			}

			const currentPageDimension = pageDimensions[currentScrollPage - 1];
			const currentPageHeight = ((currentPageDimension?.height ?? 1) * zoom) / 100;
			const offsetInPage = Math.max(0, Math.min(1, (scrollTop - pageTopOffset) / currentPageHeight));

			debouncedScrollSync(onScrollPosition, {
				page: currentScrollPage,
				offsetInPage,
				totalPages: numberOfPages,
			});
		}

		if (typeof onScroll === "function") {
			const { scrollHeight: totalScrollHeight, clientHeight } = container;
			const maxScroll = totalScrollHeight - clientHeight;
			const ratio = maxScroll > 0 ? scrollTop / maxScroll : 0;
			debouncedScrollSync(onScroll, ratio);
		}
	}, [
		scrollLocked,
		disableSyncRef,
		siblingScrollRef,
		siblingPageDimensions,
		siblingZoom,
		pageDimensions,
		zoom,
		numberOfPages,
		onScroll,
		onScrollPosition,
		loadPage,
		loadMetadata,
	]);

	useEffect(() => {
		if (
			viewportCenterBeforeZoomRef.current !== null &&
			internalScrollRef?.current &&
			previousZoomRef.current !== zoom
		) {
			const container = internalScrollRef.current;
			const { absoluteTopAtCenter, oldZoom } = viewportCenterBeforeZoomRef.current;

			const zoomRatio = zoom / oldZoom;

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					const newAbsoluteTopAtCenter = absoluteTopAtCenter * zoomRatio;

					const newScrollTop = newAbsoluteTopAtCenter - container.clientHeight / 2;

					container.scrollTop = Math.max(0, newScrollTop);

					viewportCenterBeforeZoomRef.current = null;
					previousZoomRef.current = zoom;
				});
			});
		}
	}, [zoom]);

	const handleZoomIn = useCallback(() => {
		if (internalScrollRef?.current) {
			const container = internalScrollRef.current;
			const { scrollTop, clientHeight } = container;

			const absoluteTopAtCenter = scrollTop + clientHeight / 2;

			viewportCenterBeforeZoomRef.current = {
				absoluteTopAtCenter,
				oldZoom: zoom,
			};
		}
		const newZoom = Math.min(zoom + 10, 300);
		if (onZoomChange) {
			onZoomChange(newZoom);
		} else {
			setInternalZoom(newZoom);
		}
	}, [zoom, onZoomChange]);

	const handleZoomOut = useCallback(() => {
		if (internalScrollRef?.current) {
			const container = internalScrollRef.current;
			const { scrollTop, clientHeight } = container;

			const absoluteTopAtCenter = scrollTop + clientHeight / 2;

			viewportCenterBeforeZoomRef.current = {
				absoluteTopAtCenter,
				oldZoom: zoom,
			};
		}
		const newZoom = Math.max(zoom - 10, 25);
		if (onZoomChange) {
			onZoomChange(newZoom);
		} else {
			setInternalZoom(newZoom);
		}
	}, [zoom, onZoomChange]);

	const handleHoverContent = useCallback((area) => {
		setHoveredContent(area);
	}, []);

	const handleClickContent = useCallback(
		(area) => {
			if (!enableContentClick) {
				return;
			}

			setSelectedContent(area);
			if (onContentClick && area) {
				onContentClick(area, { page: currentPage });
			}
		},
		[onContentClick, currentPage, enableContentClick]
	);

	const totalHeight = useMemo(
		() => ((pageDimensions || []).reduce((acc, curr) => acc + curr.height, 0) * zoom) / 100 || 0,
		[pageDimensions, zoom]
	);

	const pageInputWidth = useMemo(() => {
		const digits = currentPage.toString().length;
		return `${11 + digits * 12}px`;
	}, [currentPage]);

	const renderPage = useCallback(
		(dimensions, index) => {
			const page = index + 1;
			const row = content.find((c) => c.page === page);
			const effectiveWidth = (dimensions.width * zoom) / 100 || 0;
			const effectiveHeight = (dimensions.height * zoom) / 100 || 0;
			const { image, isLoading: pageLoading, error: pageError, metadata } = row || {};

			return (
				<div
					key={`${containerId}_page_${page}_z${zoomBucket}`}
					className={styles.pageRow}
					id={`${containerId}_row_${page}`}
					style={{ minHeight: effectiveHeight, width: effectiveWidth }}
				>
					<VisibilitySensor
						intervalCheck
						partialVisibility
						scrollCheck
						active={containerMounted && !isLoading}
						containment={containerMounted ? containerRef.current : undefined}
						intervalDelay={150}
						minTopValue={50}
						offset={{ top: -100, bottom: -100 }}
						scrollDelay={100}
						onChange={(isVisible) => handleVisibilityChange(isVisible, index)}
					>
						<div
							className={styles.pageImageContainer}
							style={{ width: effectiveWidth, height: effectiveHeight, position: "relative" }}
						>
							{row && pageLoading && (
								<div
									className={styles.pageLoader}
									style={{ width: effectiveWidth, height: effectiveHeight }}
								>
									<CircularLoader color="primary" size={60} />
								</div>
							)}
							{row && !pageLoading && image && (
								<>
									<img
										alt={`Page ${page}`}
										className={styles.pageImage}
										draggable={false}
										height={effectiveHeight}
										src={image}
										width={effectiveWidth}
									/>
									<SimpleContentLayer
										height={effectiveHeight}
										hoveredContent={hoveredContent}
										initialSelectedContent={initialSelectedContent}
										metadata={metadata}
										selectedContent={selectedContent}
										width={effectiveWidth}
										onClickContent={handleClickContent}
										onHoverContent={handleHoverContent}
									/>
								</>
							)}
							{row && !pageLoading && pageError && (
								<div
									className={styles.pageError}
									style={{ width: effectiveWidth, height: effectiveHeight }}
								>
									{translate("common:pdf-image.unavailable", { page })}
								</div>
							)}
							{!row && (
								<div
									className={`${styles.pageLoader} ${styles.pagePlaceholder}`}
									style={{ width: effectiveWidth, height: effectiveHeight }}
								/>
							)}
						</div>
					</VisibilitySensor>
				</div>
			);
		},
		[
			containerId,
			containerMounted,
			content,
			handleClickContent,
			handleHoverContent,
			handleVisibilityChange,
			hoveredContent,
			initialSelectedContent,
			isLoading,
			selectedContent,
			zoom,
			zoomBucket,
		]
	);

	return (
		<div className={styles.comparisonPanel}>
			<div className={styles.comparisonPanelHeader}>
				{showVersionInfo && (
					<div className={styles.versionInfo}>
						<span className={styles.documentNameWrapper}>
							<CustomTooltip showWhenOverflow title={documentName}>
								<span className={styles.documentName}>{documentName}</span>
							</CustomTooltip>
							{isLatestVersion && (
								<CustomChips
									color="info"
									label={translate(
										"common:cmp-details.option-panel.versioning.comparison.latest-version"
									)}
									size="sm"
								/>
							)}
						</span>
					</div>
				)}

				<div className={styles.controls}>
					{showZoomControls && (
						<div className={styles.zoomControls}>
							<CustomIconButton
								icon={icon.faMinusCircle}
								iconColor="var(--grey-800)"
								size="sm"
								onClick={handleZoomOut}
							/>
							{`${Math.trunc(zoom)}%`}
							<CustomIconButton
								icon={icon.faPlusCircle}
								iconColor="var(--grey-800)"
								size="sm"
								onClick={handleZoomIn}
							/>
						</div>
					)}
					{showPageInfo && documentId && (
						<div className={styles.pageInfo}>
							<CustomTextField
								inputProps={{
									className: styles.pageInputPadding,
									readOnly: true,
								}}
								style={{ width: pageInputWidth }}
								type="text"
								value={currentPage}
								variant="outlined"
							/>
							<span>/{numberOfPages}</span>
						</div>
					)}

					{headerExtra}
				</div>
			</div>
			<div
				ref={internalScrollRef}
				className={styles.comparisonPanelContent}
				id={containerId}
				onScroll={handleScroll}
			>
				{isLoading ? (
					<div className={styles.loadingPlaceholder}>
						<CircularLoader color="primary" size={60} />
						<span>{translate(`${translationPrefix}.loading`)}</span>
					</div>
				) : (
					<div className={styles.pagesContainer} style={{ minHeight: totalHeight }}>
						{pageDimensions.map((dimensions, index) => renderPage(dimensions, index))}
					</div>
				)}
			</div>
		</div>
	);
}
